import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderPageData } from '../../api';
import { OrderPageStatus } from '../../api/models/OrderPageStatus';
import { getOrderPages } from '../../utils/AccountUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import { useLanguage } from '../../common/GeneralUtils';
import {
  ALL_PAYMENT_STATUSES,
  ALL_VOUCHER_STATUSES,
  ALL_VOUCHER_TYPES,
  useVouchersFilter,
} from './VouchersFilterContext';

type SummaryLabel = { primary: string; secondary: string };

export const useVouchersFilterSummary = (): SummaryLabel => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { isFilterActive, statuses, paymentStatuses, orderPageIds, types } = useVouchersFilter();

  return useMemo(() => {
    if (!isFilterActive) return { primary: t('vouchers.filter.label'), secondary: '' };

    const parts: string[] = [];

    if (statuses.length > 0 && statuses.length < ALL_VOUCHER_STATUSES.length) {
      parts.push(statuses.map((s) => t(`vouchers.voucherstatus.${s}`)).join(', '));
    }

    if (types.length > 0 && types.length < ALL_VOUCHER_TYPES.length) {
      parts.push(types.map((type) => t(`vouchers.types.${type}`)).join(', '));
    }

    if (orderPageIds !== null) {
      const activeOrderPages = getOrderPages(account).filter(
        (op: OrderPageData) => op.status !== OrderPageStatus.ARCHIVED,
      );
      const selectedCount = orderPageIds.length;
      const total = activeOrderPages.length;
      parts.push(t('vouchers.filter.summary.workshops', { count: selectedCount, total }));
    }

    const secondaryParts: string[] = [];
    if (paymentStatuses.length > 0 && paymentStatuses.length < ALL_PAYMENT_STATUSES.length) {
      secondaryParts.push(paymentStatuses.map((s) => t(`vouchers.payment_status.${s}`)).join(', '));
    }

    return {
      primary: parts.join(' · ') || t('vouchers.filter.label'),
      secondary: secondaryParts.join(' · '),
    };
  }, [isFilterActive, statuses, paymentStatuses, orderPageIds, types, account, language, t]);
};
